import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { formatTrackLimit, formatCollaboratorLimit } from '../constants/tierLimits';
import { displayPriceForTier, priceIdForTier, type PaidTier } from '../constants/stripePrices';
import { logInfo, logError } from '../services/logtail';
import { createCheckoutSession, createUpgradeSession } from '../services/stripeService';
import CassetteLoader from '../components/CassetteLoader';
import ga from '../services/analytics';
import { useAuth } from '../contexts/AuthContext';
import RequireAccountModal from '../components/RequireAccountModal';

const PARTNER_EMAIL = 'cassette.track.management@gmail.com';

function isCurrentPlan(
  userType: string,
  includesCassetteCloud: boolean,
  tier: PaidTier,
  withCloud: boolean,
): boolean {
  return userType === tier && includesCassetteCloud === withCloud;
}

const Upgrade: React.FC = () => {
  const {
    userType,
    includesCassetteCloud,
    applyPromoCode,
    promoCode,
    promoExpiry,
    loading,
    isAnonymous,
    linkWithGoogle,
    linkWithApple,
  } = useAuth();
  const [showRequireAccount, setShowRequireAccount] = useState(false);
  const [promoOpen, setPromoOpen] = useState(false);
  const [promoInput, setPromoInput] = useState('');
  const [promoMsg, setPromoMsg] = useState<string | null>(null);
  const [promoLoading, setPromoLoading] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [loadingTier, setLoadingTier] = useState<string | null>(null);
  const [artistIncludesCloud, setArtistIncludesCloud] = useState(includesCassetteCloud);
  const [studioIncludesCloud, setStudioIncludesCloud] = useState(includesCassetteCloud);
  const { t } = useTranslation();

  if (loading) {
    return <CassetteLoader />;
  }

  const handleSubscribe = async (tier: PaidTier, withCloud: boolean) => {
    if (isAnonymous) {
      setShowRequireAccount(true);
      return;
    }
    const loadingKey = `${tier}-${withCloud ? 'cloud' : 'byod'}`;
    setLoadingTier(loadingKey);
    setIsLoading(true);
    try {
      logInfo('User initiated subscription', { tier, withCloud, userType });
      const price = displayPriceForTier(tier, withCloud);
      ga.event('subscription_checkout_attempt', {
        plan: tier,
        price,
        currency: 'USD',
        from_tier: userType,
        to_tier: tier,
        includes_cassette_cloud: withCloud,
      });

      const priceId = priceIdForTier(tier, withCloud);
      if (!priceId) {
        throw new Error(`Stripe price ID for ${tier}${withCloud ? '+cloud' : ''} not configured`);
      }

      let response;
      if (userType === 'artist' || userType === 'studio') {
        logInfo('User has existing subscription, using upgrade session', { tier, priceId, withCloud });
        response = await createUpgradeSession(priceId);
      } else {
        logInfo('New subscription, using checkout session', { tier, priceId, withCloud });
        response = await createCheckoutSession(priceId);
      }

      if (response.success && response.sessionUrl) {
        logInfo('Session created successfully', { tier, userType, withCloud });
        window.location.href = response.sessionUrl;
      } else {
        throw new Error(response.errorMessage || 'Failed to create session');
      }
    } catch (error) {
      logError('Failed to create session', { error, tier, userType, withCloud });
      alert('Failed to start payment process. Please try again.');
    } finally {
      setIsLoading(false);
      setLoadingTier(null);
    }
  };

  const subscribeButtonLabel = (tier: PaidTier, withCloud: boolean) => {
    const tierName = t(`upgrade.${tier}`);
    if (withCloud) {
      return t('upgrade.subscribeWithCloud', { tier: tierName });
    }
    return `${t('upgrade.subscribe')} ${tierName}`;
  };

  const renderCloudToggle = (tier: PaidTier, checked: boolean, onChange: (value: boolean) => void) => {
    const accentClass = tier === 'studio' ? 'accent-purple-600' : 'accent-emerald-600';
    const checkedBorderClass = tier === 'studio' ? 'border-purple-500' : 'border-emerald-500';

    return (
      <div className="mt-4">
        <label
          className={`flex items-start gap-3 p-4 rounded-xl border-2 cursor-pointer transition-colors ${
            checked ? checkedBorderClass : ''
          }`}
          style={
            checked
              ? { backgroundColor: 'var(--color-hover)' }
              : { borderColor: 'var(--color-border)', backgroundColor: 'var(--color-hover)' }
          }
        >
          <input
            type="checkbox"
            className={`mt-0.5 w-6 h-6 shrink-0 cursor-pointer ${accentClass}`}
            checked={checked}
            onChange={(e) => onChange(e.target.checked)}
            data-testid={`${tier}-cloud-toggle`}
          />
          <span className="text-left flex-1 min-w-0" style={{ color: 'var(--color-text)' }}>
            <span className="font-semibold block text-base leading-snug">{t('upgrade.includeCassetteCloud')}</span>
            <span className="text-sm text-[var(--color-muted)] mt-1 block leading-snug">
              {t('upgrade.cassetteCloudAddon', { price: `$${displayPriceForTier(tier, true)}` })}
            </span>
          </span>
        </label>
        {checked && (
          <p className="text-xs text-[var(--color-muted)] mt-2 px-1">{t('upgrade.cassetteCloudWhyPricier')}</p>
        )}
      </div>
    );
  };

  const artistCurrent = isCurrentPlan(userType, includesCassetteCloud, 'artist', artistIncludesCloud);
  const studioCurrent = isCurrentPlan(userType, includesCassetteCloud, 'studio', studioIncludesCloud);

  return (
    <div className="bg-[var(--color-bg)] flex flex-col flex-1">
      <div className="flex flex-col items-center justify-center flex-1 px-2 py-8">
      <h1 className="text-3xl font-bold mb-2 text-center text-[var(--color-text)]">{t('upgrade.choosePlan')}</h1>
      <div className="max-w-2xl mb-6 space-y-3 text-center">
        <p className="text-sm text-[var(--color-muted)]">{t('upgrade.byodNote')}</p>
        <p className="text-sm text-[var(--color-text)] rounded-lg border px-4 py-3 text-left" style={{ borderColor: 'var(--color-border)', backgroundColor: 'var(--color-hover)' }}>
          {t('upgrade.byodWhyCheaper')}
        </p>
      </div>
      <div className="flex flex-col lg:flex-row gap-6 w-full max-w-5xl">
        <div className={`flex-1 border-2 rounded-xl p-6 shadow relative flex flex-col ${
          userType === 'starter'
            ? 'border-[var(--brand-from)]'
            : 'border-[var(--color-border)] bg-[var(--color-surface)]'
        }`} style={userType === 'starter' ? {
          backgroundColor: 'var(--color-surface)',
          borderColor: 'var(--brand-from)'
        } : {}}>
          {userType === 'starter' && (
            <div className="absolute -top-4 left-1/2 -translate-x-1/2 px-4 py-1 rounded-full text-xs font-bold shadow" style={{ backgroundColor: 'var(--brand-from)', color: 'white' }}>
              Current Plan
            </div>
          )}
          <h2 className="text-2xl font-bold mb-2" style={{ color: 'var(--color-text)' }}>{t('upgrade.starter')}</h2>
          <div className="text-3xl font-bold mb-4" style={{ color: 'var(--color-text)' }}>{t('upgrade.pricing.free')}</div>
          <ul className="mb-6 space-y-2 text-sm" style={{ color: 'var(--color-text)' }}>
            <li>✔️ {t('upgrade.features.fullTrackEditing')}</li>
            <li>✔️ {formatTrackLimit('starter')}</li>
            <li>✔️ {formatCollaboratorLimit('starter')}</li>
            <li>✔️ {t('upgrade.features.tryFullFlow')}</li>
          </ul>
          <div className="mt-auto text-center">
            {userType === 'starter' ? (
              <span className="text-sm font-semibold" style={{ color: 'var(--brand-from)' }}>
                ✓ Your Current Plan
              </span>
            ) : (
              <span className="text-[var(--color-muted)] text-sm">{t('upgrade.noCreditCard')}</span>
            )}
          </div>
        </div>

        <div className={`flex-1 border-2 rounded-xl p-6 shadow-lg relative flex flex-col ${
          userType === 'artist' && includesCassetteCloud === artistIncludesCloud
            ? 'border-[var(--brand-from)]'
            : 'border-emerald-500 bg-[var(--color-surface)]'
        }`}>
          <div className="absolute -top-4 left-1/2 -translate-x-1/2 px-4 py-1 rounded-full text-xs font-bold shadow" style={{
            backgroundColor: artistCurrent ? 'var(--brand-from)' : '#059669',
            color: 'white',
          }}>
            {artistCurrent ? 'Current Plan' : t('upgrade.badges.mostPopular')}
          </div>
          <h2 className="text-2xl font-bold mb-2" style={{ color: 'var(--color-text)' }}>{t('upgrade.artist')}</h2>
          <div className="text-3xl font-bold text-[var(--color-text)] mb-2">
            ${displayPriceForTier('artist', artistIncludesCloud)}{t('upgrade.pricing.monthly')}
          </div>
          <ul className="mb-2 space-y-2 text-sm" style={{ color: 'var(--color-text)' }}>
            <li>✔️ {t('upgrade.features.fullTrackEditing')}</li>
            <li>✔️ {formatTrackLimit('artist')}</li>
            <li>✔️ {formatCollaboratorLimit('artist')}</li>
            <li>✔️ {t('upgrade.features.allFeaturesIncluded')}</li>
          </ul>
          {renderCloudToggle('artist', artistIncludesCloud, setArtistIncludesCloud)}
          {artistCurrent ? (
            <div className="mt-auto pt-4 text-center">
              <span className="text-sm font-semibold" style={{ color: 'var(--color-text)' }}>✓ Your Current Plan</span>
            </div>
          ) : (
            <div className="mt-auto pt-4 w-full">
            <button
              onClick={() => handleSubscribe('artist', artistIncludesCloud)}
              disabled={isLoading}
              className="block w-full px-6 py-3 text-lg font-bold text-white text-center transition disabled:opacity-50 disabled:cursor-not-allowed bg-emerald-600 hover:bg-emerald-700 rounded-lg"
              data-testid="subscribe-artist-button"
            >
              {isLoading && loadingTier === `artist-${artistIncludesCloud ? 'cloud' : 'byod'}` ? (
                <span className="flex items-center gap-2 justify-center gap-2">
                  <CassetteLoader size={48} variant="inline" />
                  {t('upgrade.processing')}
                </span>
              ) : (
                subscribeButtonLabel('artist', artistIncludesCloud)
              )}
            </button>
            </div>
          )}
        </div>

        <div className={`flex-1 border-2 rounded-xl p-6 shadow-lg relative flex flex-col ${
          userType === 'studio' && includesCassetteCloud === studioIncludesCloud
            ? 'border-[var(--brand-from)]'
            : 'border-purple-500 bg-[var(--color-surface)]'
        }`}>
          <div className="absolute -top-4 left-1/2 -translate-x-1/2 px-4 py-1 rounded-full text-xs font-bold shadow" style={{
            backgroundColor: studioCurrent ? 'var(--brand-from)' : '#9333ea',
            color: 'white',
          }}>
            {studioCurrent ? 'Current Plan' : t('upgrade.badges.forTeams')}
          </div>
          <h2 className="text-2xl font-bold mb-2" style={{ color: 'var(--color-text)' }}>{t('upgrade.studio')}</h2>
          <div className="text-3xl font-bold mb-2" style={{ color: 'var(--color-text)' }}>
            ${displayPriceForTier('studio', studioIncludesCloud)}{t('upgrade.pricing.monthly')}
          </div>
          <ul className="mb-2 space-y-2 text-sm" style={{ color: 'var(--color-text)' }}>
            <li>✔️ {t('upgrade.features.fullTrackEditing')}</li>
            <li>✔️ {formatTrackLimit('studio')}</li>
            <li>✔️ {formatCollaboratorLimit('studio')}</li>
            <li>✔️ {t('upgrade.features.futureFeaturesAutoAdded')}</li>
          </ul>
          {renderCloudToggle('studio', studioIncludesCloud, setStudioIncludesCloud)}
          {studioCurrent && !promoCode ? (
            <div className="mt-auto pt-4 text-center">
              <span className="text-sm font-semibold" style={{ color: 'var(--brand-from)' }}>✓ Your Current Plan</span>
            </div>
          ) : (
            <div className="mt-auto pt-4 w-full">
            <button
              onClick={() => handleSubscribe('studio', studioIncludesCloud)}
              disabled={isLoading}
              className="block w-full px-6 py-3 text-lg font-bold text-white text-center transition disabled:opacity-50 disabled:cursor-not-allowed bg-purple-600 hover:bg-purple-700 rounded-lg"
              data-testid="subscribe-studio-button"
            >
              {isLoading && loadingTier === `studio-${studioIncludesCloud ? 'cloud' : 'byod'}` ? (
                <span className="flex items-center gap-2 justify-center gap-2">
                  <CassetteLoader size={48} variant="inline" />
                  {t('upgrade.processing')}
                </span>
              ) : (
                subscribeButtonLabel('studio', studioIncludesCloud)
              )}
            </button>
            </div>
          )}
        </div>
      </div>

      <div className="mt-8 text-center">
        <div className="inline-flex items-center gap-2 px-6 py-3 rounded-lg border-2 border-dashed" style={{ borderColor: 'var(--color-border)', backgroundColor: 'var(--color-hover)' }}>
          <span className="text-[var(--color-muted)] text-sm">
            {t('upgrade.havePromo')}
          </span>
          <button
            className="px-4 py-2 rounded-lg font-semibold text-sm transition-colors"
            style={{
              backgroundColor: 'var(--brand-from)',
              color: 'white',
            }}
            onClick={() => {
              logInfo('User opened promo code modal');
              setPromoOpen(true);
              setPromoMsg(null);
              setPromoInput('');
            }}
          >
            {t('upgrade.applyPromo')}
          </button>
        </div>
      </div>

      {promoOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-30">
          <div
            className="rounded-xl shadow-lg p-6 w-full max-w-xs flex flex-col items-center border"
            style={{ backgroundColor: 'var(--color-surface)', borderColor: 'var(--color-border)' }}
            data-testid="promo-modal"
          >
            <h3 className="text-lg font-bold mb-2" style={{ color: 'var(--color-text)' }}>{t('upgrade.enterPromo')}</h3>
            <input
              type="text"
              value={promoInput}
              onChange={(e) => setPromoInput(e.target.value)}
              className="border rounded px-3 py-2 w-full mb-2 bg-transparent"
              style={{ borderColor: 'var(--color-border)', color: 'var(--color-text)' }}
              placeholder={t('upgrade.promoPlaceholder')}
              disabled={promoLoading}
              data-testid="promo-input"
            />
            {promoMsg && <div className={`mb-2 text-sm ${promoMsg.startsWith('Promo code applied') ? 'text-green-600' : 'text-red-600'}`}>{promoMsg}</div>}
            <div className="flex gap-2 w-full">
              <button
                className="flex-1 px-3 py-2 rounded text-white font-semibold transition"
                style={{ backgroundColor: 'var(--brand-from)' }}
                disabled={promoLoading}
                data-testid="promo-apply-button"
                onClick={async () => {
                  logInfo('User applying promo code', { code: promoInput.trim() });
                  setPromoLoading(true);
                  const res = await applyPromoCode(promoInput.trim());
                  setPromoMsg(res.message);
                  setPromoLoading(false);
                  if (res.success) {
                    logInfo('Promo code applied successfully from upgrade page', { code: promoInput.trim() });
                    setTimeout(() => setPromoOpen(false), 1200);
                  } else {
                    logInfo('Promo code application failed from upgrade page', { code: promoInput.trim() });
                  }
                }}
              >
                {t('upgrade.apply')}
              </button>
              <button
                className="flex-1 px-3 py-2 rounded font-semibold transition"
                style={{ backgroundColor: 'var(--color-hover)', color: 'var(--color-text)' }}
                onClick={() => {
                  logInfo('User cancelled promo code modal');
                  setPromoOpen(false);
                }}
                disabled={promoLoading}
                data-testid="promo-cancel-button"
              >
                {t('upgrade.cancel')}
              </button>
            </div>
          </div>
        </div>
      )}
      <div className="mt-8 text-center text-xs" style={{ color: 'var(--color-muted)' }}>
        * {t('upgrade.disclaimer')}
      </div>
      {(userType === 'artist' || userType === 'studio') && promoExpiry && (
        <div className="mt-2 text-xs text-emerald-700">{t('upgrade.premiumViaPromo', { date: promoExpiry.toLocaleDateString() })}</div>
      )}
      <div className="mt-8 text-center text-sm" style={{ color: 'var(--color-text)' }}>
        <strong>{t('upgrade.musicianCtaTitle')}</strong><br />
        {t('upgrade.musicianCtaText', { email: PARTNER_EMAIL })}
      </div>
      </div>
      <RequireAccountModal
        isOpen={showRequireAccount}
        onClose={() => setShowRequireAccount(false)}
        onLinkGoogle={() => { setShowRequireAccount(false); void linkWithGoogle(); }}
        onLinkApple={() => { setShowRequireAccount(false); void linkWithApple(); }}
      />
    </div>
  );
};

export default Upgrade;
